import Link from 'next/link'
import { ReactNode, useMemo } from 'react'
import { RankType } from '@/api/workspaces-api'
import useBillboardMultiDimensionParams from '@/components/billboard/hooks/use-billboard-multi-dimension-params'
import useLinkListSortedCountries from '@/components/billboard/hooks/use-link-list-sorted-countries'
import useLinkListTitle from '@/components/billboard/hooks/use-link-list-title'
import Header from '@/components/billboard/link-list/header'
import LinkListLayout from '@/components/billboard/link-list/link-list-layout'
import { COUNTRY_I18N_MAP } from '@/constants/country-code'
import routes from '@/hooks/use-authorization/routes'
import { I18nId } from '@/i18n/config'
import { useIntl } from '@/i18n/hooks/use-intl'
import {
  LinkListWrapper,
  Wrapper,
} from '@/pages/billboard/[platform]/[country]/customized-tag'
import { NextLayoutComponentType } from '@/types/next-layout-component-type'
import {
  BillboardStatistic,
  billboardStatisticSchema,
  LinkListPlatforms,
} from '@/types/schema/billboard/multi-dimension'

export const STATISTIC_I18N_MAP: Record<BillboardStatistic, I18nId> = {
  'community-volume': 'landing:billboard_community_volume',
  'engagement-rate': 'landing:billboard_engagement_rate',
  'view-rate': 'landing:billboard_view_rate',
} as const

export const STATISTIC_RANK_TYPE_MAP: Record<BillboardStatistic, RankType> = {
  'community-volume': RankType.WordOfMouthVolume,
  'engagement-rate': RankType.EngagementRate,
  'view-rate': RankType.ViewRate,
} as const

const StatisticBillboardLinkList: NextLayoutComponentType = () => {
  const { formatMessage } = useIntl()
  const countries = useLinkListSortedCountries()
  const searchParams = routes.billboardStatisticList.useSearchParams()
  const { platform, country, platformTitle } =
    useBillboardMultiDimensionParams()
  const { getLinkTitle } = useLinkListTitle()

  const linkList = useMemo(() => {
    return countries.flatMap((country) => {
      return billboardStatisticSchema.options.map((statistic) => {
        const countryLocale = formatMessage({ id: COUNTRY_I18N_MAP[country] })
        const levelLocale = formatMessage({ id: STATISTIC_I18N_MAP[statistic] })

        return {
          title: getLinkTitle(platformTitle, [countryLocale, levelLocale]),
          url: routes.billboardStatisticDetail.stringifyUrl(
            {
              platform,
              country,
              statistic,
            },
            { lang: searchParams.data?.lang },
          ),
        }
      })
    })
  }, [
    countries,
    formatMessage,
    getLinkTitle,
    platformTitle,
    platform,
    searchParams.data?.lang,
  ])

  const switchPlatform = async (platform: LinkListPlatforms): Promise<void> => {
    await routes.billboardStatisticList.goto(
      {
        platform,
        country,
      },
      { ...searchParams.data },
    )
  }

  return (
    <Wrapper>
      <Header
        breadcrumbItems={[
          {
            title: `${platformTitle} ${formatMessage({
              id: 'general:navbar_item_search__kol_by_country',
            })}`,
          },
        ]}
        pageTitle={`${platformTitle} ${formatMessage({
          id: 'landing:general_influencer_billboard_country',
        })}`}
        onChangePlatform={switchPlatform}
      />
      <LinkListWrapper column={3}>
        {linkList.map((item) => (
          <Link href={item.url} key={item.url}>
            {item.title}
          </Link>
        ))}
      </LinkListWrapper>
    </Wrapper>
  )
}

StatisticBillboardLinkList.getLayout = (page): ReactNode => {
  return <LinkListLayout from='Billboard list'>{page}</LinkListLayout>
}

export default StatisticBillboardLinkList
